<template>
  <div class="v-row d-flex flex-column gap-4">
    <div
      v-for="localField of localFields"
      :key="`${localField.triggerId}-${localField.formFieldId}`"
      class="d-flex flex-row gap-2">
      <h3 v-if="localField.customField.label">
        {{ localField.customField.label }}:
        <span v-if="localField.customField.required" class="text-error">*</span>
        <help-icon-tooltip v-if="localField.customField.description">
          {{ localField.customField.description }}
        </help-icon-tooltip>
      </h3>
      <!-- File fields with a pre-existing cloned value are shown as links -->
      <template v-if="isFileFieldWithValue(localField)">
        <div class="d-flex flex-column gap-1">
          <template v-if="Array.isArray(localField.value)">
            <a
              v-for="url in localField.value"
              :key="url"
              :href="fileHrefFromValue(url)"
              target="_blank"
              rel="noopener noreferrer">
              {{ fileNameFromUrl(url) }}
            </a>
          </template>
          <a
            v-else
            :href="fileHrefFromValue(localField.value)"
            target="_blank"
            rel="noopener noreferrer">
            {{ fileNameFromUrl(localField.value) }}
          </a>
        </div>
      </template>
      <component
        v-else
        v-bind="$attrs"
        v-bind:is="customFieldComponents[localField.customField.type]"
        :field="localField.customField"
        v-model="localField.value"
        single-line
        :timezone="warehouse.timezone"
        :military-time-enabled="$isMilitaryTimeEnabled(warehouse)"
        :warehouse="warehouse" />
    </div>
  </div>
</template>

<script>
import { defineComponent, onMounted, ref, watch } from 'vue';
import {
  CustomFormDataEntitiesEnum,
  fileKeyToDownloadUrl,
  getDocumentNameFromUrl
} from '@nova/core';
import { useStore } from 'vuex';
import { useCustomForms } from '@/composables';

import CustomFieldAction from '@/modules/custom_fields/components/field_types/CustomFieldAction.vue';
import CustomFieldBigstring from '@/modules/custom_fields/components/field_types/CustomFieldBigstring.vue';
import CustomFieldBool from '@/modules/custom_fields/components/field_types/CustomFieldBool.vue';
import CustomFieldCombobox from '@/modules/custom_fields/components/field_types/CustomFieldCombobox.vue';
import CustomFieldDate from '@/modules/custom_fields/components/field_types/CustomFieldDate.vue';
import CustomFieldDocument from '@/modules/custom_fields/components/field_types/CustomFieldDocument.vue';
import CustomFieldDropdownmultiselect from '@/modules/custom_fields/components/field_types/CustomFieldDropdownmultiselect.vue';
import CustomFieldDropdown from '@/modules/custom_fields/components/field_types/CustomFieldDropdown.vue';
import CustomFieldEmail from '@/modules/custom_fields/components/field_types/CustomFieldEmail.vue';
import CustomFieldMultidocument from '@/modules/custom_fields/components/field_types/CustomFieldMultidocument.vue';
import CustomFieldNumber from '@/modules/custom_fields/components/field_types/CustomFieldNumber.vue';
import CustomFieldPhone from '@/modules/custom_fields/components/field_types/CustomFieldPhone.vue';
import CustomFieldString from '@/modules/custom_fields/components/field_types/CustomFieldString.vue';
import CustomFieldTimestamp from '@/modules/custom_fields/components/field_types/CustomFieldTimestamp.vue';

export default defineComponent({
  name: 'CreateAppointmentCustomForms',
  emits: ['update:value'],
  props: {
    triggers: {
      type: Array,
      required: true
    },
    warehouse: {
      type: Object,
      required: true
    },
    initialCustomFormsData: {
      type: Array,
      default: () => []
    }
  },
  components: {
    CustomFieldAction,
    CustomFieldBigstring,
    CustomFieldBool,
    CustomFieldCombobox,
    CustomFieldDate,
    CustomFieldDocument,
    CustomFieldDropdownmultiselect,
    CustomFieldDropdown,
    CustomFieldEmail,
    CustomFieldMultidocument,
    CustomFieldNumber,
    CustomFieldPhone,
    CustomFieldString,
    CustomFieldTimestamp
  },
  setup(props, { emit }) {
    const store = useStore();
    const { getCustomField, customFieldComponents } = useCustomForms();
    const localFields = ref([]);
    const propagateValueChange = () =>
      emit(
        'update:value',
        localFields.value.map(field => ({
          formFieldId: field.formFieldId,
          triggerId: field.triggerId,
          value: field.value
        }))
      );

    const FILE_FIELD_TYPES = ['doc', 'multidoc'];

    const isFileFieldWithValue = localField => {
      if (!FILE_FIELD_TYPES.includes(localField.customField.type)) return false;
      const val = localField.value;
      return Array.isArray(val) ? val.length > 0 : Boolean(val);
    };

    const fileNameFromUrl = url => getDocumentNameFromUrl(String(url)) || String(url);

    // The API returns doc URLs built with Neutron's s3BaseUrl, which may differ from the
    // frontend's (e.g. http://localhost:8080 vs https://od-nova.s3.localstack...). Extract
    // the file path from whatever base the API used and reconstruct with the frontend URL.
    const fileHrefFromValue = url => {
      const s3BaseUrl = store.getters['App/s3BaseUrl'];
      if (!s3BaseUrl || !url) return String(url);
      try {
        const fileKey = new URL(String(url)).pathname.slice(1);
        return fileKey ? fileKeyToDownloadUrl(s3BaseUrl, fileKey) : String(url);
      } catch {
        return fileKeyToDownloadUrl(s3BaseUrl, String(url));
      }
    };

    const parseClonedFieldValue = (rawValue, fieldType) => {
      if (rawValue == null) {
        return fieldType === 'bool' ? false : null;
      }
      if (fieldType === 'bool') {
        return rawValue === true || rawValue === 'true';
      }
      if (fieldType === 'dropdownmultiselect' || fieldType === 'multidoc') {
        if (Array.isArray(rawValue)) return rawValue;
        if (typeof rawValue === 'string' && rawValue.length > 0) {
          try {
            const parsed = JSON.parse(rawValue);
            return Array.isArray(parsed) ? parsed : null;
          } catch {
            return null;
          }
        }
      }
      return rawValue;
    };

    const setLocalFields = () => {
      for (const trigger of props.triggers.filter(trigger => Boolean(trigger.flow?.formFrom))) {
        for (const formField of trigger.flow.formFrom.formFields.sort(
          (a, b) => a.order - b.order
        )) {
          const fieldType = formField.field.type;
          const existingData = props.initialCustomFormsData.find(
            d => d.formFieldId === formField.id && d.triggerId === trigger.id
          );
          const defaultValue = fieldType === 'bool' ? false : null;
          const initialValue =
            existingData != null
              ? parseClonedFieldValue(existingData.value, fieldType)
              : defaultValue;

          const customField = getCustomField(formField);
          customField.value = initialValue;

          localFields.value.push({
            form: trigger.flow.formFrom,
            customField,
            entityName: CustomFormDataEntitiesEnum.appointment,
            formFieldId: formField.id,
            triggerId: trigger.id,
            value: initialValue
          });
        }
      }
    };

    // Build the initial value for the request
    onMounted(() => {
      setLocalFields();
      propagateValueChange();
    });

    watch(localFields, () => propagateValueChange(), { deep: true });

    return {
      propagateValueChange,
      localFields,
      customFieldComponents,
      isFileFieldWithValue,
      fileNameFromUrl,
      fileHrefFromValue
    };
  }
});
</script>
